import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import {
  MessageSquare,
  Send,
  Search,
  Paperclip,
  X,
  ChevronLeft,
  Users,
  UserCheck,
  UserCog,
  User as UserIcon,
  Clock,
  CheckCheck
} from 'lucide-react';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';

const MessagingSystem = ({ userRole = 'admin' }) => {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCompose, setShowCompose] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Compose message form
  const [composeData, setComposeData] = useState({
    recipient_type: 'all',
    recipient_id: null,
    subject: '',
    content: '',
    attachment: null
  });
  
  // Reply message
  const [replyContent, setReplyContent] = useState('');
  const [replyAttachment, setReplyAttachment] = useState(null);
  
  const user = JSON.parse(localStorage.getItem('user'));
  
  useEffect(() => {
    loadThreads();
    loadUnreadCount();
    
    // Poll for new messages every 30 seconds
    const interval = setInterval(() => {
      loadThreads();
      loadUnreadCount();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);
  
  const loadThreads = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/threads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThreads(response.data);
    } catch (error) {
      console.error('Failed to load message threads', error);
    }
  };
  
  const loadUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to load unread count', error);
    }
  };
  
  const loadThreadMessages = async (threadId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/thread/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
      setSelectedThread(threadId);
      
      // Reload threads to update unread count
      await loadThreads();
      await loadUnreadCount();
    } catch (error) {
      console.error('Failed to load thread messages', error);
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendMessage = async () => {
    if (!composeData.content.trim()) {
      toast.error('Message content is required');
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Upload attachment if present
      let attachmentUrl = null;
      let attachmentName = null;
      if (composeData.attachment) {
        const formData = new FormData();
        formData.append('file', composeData.attachment);
        
        const uploadRes = await axios.post(
          `${API}/messages/upload-attachment`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        attachmentUrl = uploadRes.data.attachment_url;
        attachmentName = uploadRes.data.attachment_name;
      }
      
      // Send message
      await axios.post(
        `${API}/messages`,
        {
          recipient_type: composeData.recipient_type,
          recipient_id: composeData.recipient_id,
          subject: composeData.subject || 'No subject',
          content: composeData.content,
          attachment_url: attachmentUrl,
          attachment_name: attachmentName
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Message sent successfully');
      setShowCompose(false);
      setComposeData({
        recipient_type: 'all',
        recipient_id: null,
        subject: '',
        content: '',
        attachment: null
      });
      
      await loadThreads();
    } catch (error) {
      console.error('Failed to send message', error);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };
  
  const handleReply = async () => {
    if (!replyContent.trim()) {
      toast.error('Reply content is required');
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Upload attachment if present
      let attachmentUrl = null;
      let attachmentName = null;
      if (replyAttachment) {
        const formData = new FormData();
        formData.append('file', replyAttachment);
        
        const uploadRes = await axios.post(
          `${API}/messages/upload-attachment`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        attachmentUrl = uploadRes.data.attachment_url;
        attachmentName = uploadRes.data.attachment_name;
      }
      
      // Get the other participant's ID (not current user)
      const thread = threads.find(t => t.thread_id === selectedThread);
      const otherParticipant = thread?.participants.find(p => p.id !== user.id);
      
      // Send reply
      await axios.post(
        `${API}/messages`,
        {
          thread_id: selectedThread,
          recipient_type: 'individual',
          recipient_id: otherParticipant?.id,
          subject: thread?.subject || 'Re: Message',
          content: replyContent,
          attachment_url: attachmentUrl,
          attachment_name: attachmentName
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success('Reply sent successfully');
      setReplyContent('');
      setReplyAttachment(null);
      
      // Reload thread messages
      await loadThreadMessages(selectedThread);
      await loadThreads();
    } catch (error) {
      console.error('Failed to send reply', error);
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadThreads();
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/messages/search?query=${encodeURIComponent(searchQuery)}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Group search results by thread
      const threadIds = [...new Set(response.data.map(m => m.thread_id))];
      const filteredThreads = threads.filter(t => threadIds.includes(t.thread_id));
      setThreads(filteredThreads);
    } catch (error) {
      console.error('Failed to search messages', error);
      toast.error('Search failed');
    }
  };
  
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };
  
  const getRecipientIcon = (type) => {
    switch (type) {
      case 'all': return <Users className="w-4 h-4" />;
      case 'directors': return <UserCog className="w-4 h-4" />;
      case 'teachers': return <UserCheck className="w-4 h-4" />;
      case 'individual': return <UserIcon className="w-4 h-4" />;
      default: return <UserIcon className="w-4 h-4" />;
    }
  };
  
  return (
    <div className="h-[calc(100vh-200px)] flex gap-4">
      {/* Thread List */}
      <Card className="w-1/3 flex flex-col p-0 overflow-hidden">
        <div className="p-4 border-b bg-gradient-to-r from-blue-500 to-indigo-600">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2 text-white">
              <MessageSquare className="w-6 h-6" />
              <h2 className="text-lg font-semibold">Messages</h2>
              {unreadCount > 0 && (
                <Badge className="bg-red-500 text-white">{unreadCount}</Badge>
              )}
            </div>
            {userRole === 'admin' && (
              <Button
                onClick={() => setShowCompose(true)}
                size="sm"
                variant="secondary"
                className="bg-white text-blue-600 hover:bg-blue-50"
              >
                <Send className="w-4 h-4 mr-2" />
                New
              </Button>
            )}
          </div>
          
          {/* Search */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 bg-white"
              />
            </div>
            <Button
              onClick={handleSearch}
              size="sm"
              variant="secondary"
              className="bg-white text-blue-600"
            >
              <Search className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Thread List */}
        <div className="flex-1 overflow-y-auto">
          {threads.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <MessageSquare className="w-12 h-12 mb-2 opacity-50" />
              <p className="text-sm">No messages yet</p>
            </div>
          ) : (
            threads.map((thread) => (
              <div
                key={thread.thread_id}
                onClick={() => loadThreadMessages(thread.thread_id)}
                className={`p-4 border-b cursor-pointer transition-colors hover:bg-gray-50 ${
                  selectedThread === thread.thread_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center space-x-2 flex-1">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold">
                      {thread.last_sender_name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-sm truncate">
                          {thread.subject}
                        </p>
                        {thread.unread_count > 0 && (
                          <Badge className="bg-red-500 text-white text-xs ml-2">
                            {thread.unread_count}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 truncate">
                        {thread.last_sender_name}
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-600 truncate ml-12">
                  {thread.last_message}
                </p>
                <div className="flex items-center justify-between mt-2 ml-12">
                  <div className="flex items-center space-x-1 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    <span>{formatTime(thread.last_message_time)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    {thread.participants.length > 2 && (
                      <Badge variant="outline" className="text-xs">
                        {thread.participants.length} participants
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
      
      {/* Message Thread View */}
      <Card className="flex-1 flex flex-col p-0 overflow-hidden">
        {selectedThread ? (
          <>
            {/* Thread Header */}
            <div className="p-4 border-b bg-gradient-to-r from-blue-500 to-indigo-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Button
                    onClick={() => {
                      setSelectedThread(null);
                      setMessages([]);
                    }}
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <div>
                    <h3 className="font-semibold">
                      {threads.find(t => t.thread_id === selectedThread)?.subject}
                    </h3>
                    <p className="text-xs text-blue-100">
                      {threads.find(t => t.thread_id === selectedThread)?.participants
                        .filter(p => p.id !== user.id)
                        .map(p => p.name)
                        .join(', ')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                messages.map((msg) => {
                  const isCurrentUser = msg.sender_id === user.id;
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg p-3 ${
                          isCurrentUser
                            ? 'bg-blue-500 text-white'
                            : 'bg-white text-gray-900 shadow-sm'
                        }`}
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          <p className={`text-xs font-semibold ${
                            isCurrentUser ? 'text-blue-100' : 'text-gray-600'
                          }`}>
                            {msg.sender_name}
                          </p>
                          <span className={`text-xs ${
                            isCurrentUser ? 'text-blue-200' : 'text-gray-500'
                          }`}>
                            {formatTime(msg.created_at)}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        {msg.attachment_url && (
                          <a
                            href={`${API}${msg.attachment_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center space-x-2 mt-2 text-xs underline ${
                              isCurrentUser ? 'text-blue-100' : 'text-blue-600'
                            }`}
                          >
                            <Paperclip className="w-3 h-3" />
                            <span>{msg.attachment_name || 'Attachment'}</span>
                          </a>
                        )}
                        {isCurrentUser && msg.is_read && (
                          <div className="flex items-center justify-end mt-1">
                            <CheckCheck className="w-3 h-3 text-blue-200" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            {/* Reply Box */}
            <div className="p-4 border-t bg-white">
              <div className="flex flex-col space-y-2">
                {replyAttachment && (
                  <div className="flex items-center justify-between p-2 bg-gray-100 rounded">
                    <div className="flex items-center space-x-2 text-sm">
                      <Paperclip className="w-4 h-4" />
                      <span>{replyAttachment.name}</span>
                    </div>
                    <Button
                      onClick={() => setReplyAttachment(null)}
                      variant="ghost"
                      size="sm"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                <div className="flex space-x-2">
                  <Input
                    type="file"
                    id="reply-attachment"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 2 * 1024 * 1024) {
                          toast.error('File size must be less than 2MB');
                          return;
                        }
                        setReplyAttachment(file);
                      }
                    }}
                  />
                  <Button
                    onClick={() => document.getElementById('reply-attachment').click()}
                    variant="outline"
                    size="sm"
                  >
                    <Paperclip className="w-4 h-4" />
                  </Button>
                  <Textarea
                    placeholder="Type your reply..."
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    className="flex-1 min-h-[60px] max-h-[120px]"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleReply();
                      }
                    }}
                  />
                  <Button
                    onClick={handleReply}
                    disabled={loading || !replyContent.trim()}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <MessageSquare className="w-16 h-16 mb-4 opacity-50" />
            <p className="text-lg font-medium">Select a conversation</p>
            <p className="text-sm">Choose a message thread to view and reply</p>
          </div>
        )}
      </Card>
      
      {/* Compose Message Dialog */}
      <Dialog open={showCompose} onOpenChange={setShowCompose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Compose New Message</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Recipient Selection */}
            <div>
              <Label>Send to</Label>
              <RadioGroup
                value={composeData.recipient_type}
                onValueChange={(value) =>
                  setComposeData({ ...composeData, recipient_type: value })
                }
                className="mt-2 space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="all" id="all" />
                  <Label htmlFor="all" className="flex items-center space-x-2 cursor-pointer">
                    <Users className="w-4 h-4" />
                    <span>All (Directors + Teachers)</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="directors" id="directors" />
                  <Label htmlFor="directors" className="flex items-center space-x-2 cursor-pointer">
                    <UserCog className="w-4 h-4" />
                    <span>Franchise Directors Only</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="teachers" id="teachers" />
                  <Label htmlFor="teachers" className="flex items-center space-x-2 cursor-pointer">
                    <UserCheck className="w-4 h-4" />
                    <span>All Teachers Only</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>
            
            {/* Subject */}
            <div>
              <Label>Subject</Label>
              <Input
                placeholder="Message subject..."
                value={composeData.subject}
                onChange={(e) =>
                  setComposeData({ ...composeData, subject: e.target.value })
                }
                className="mt-1"
              />
            </div>
            
            {/* Content */}
            <div>
              <Label>Message</Label>
              <Textarea
                placeholder="Type your message here..."
                value={composeData.content}
                onChange={(e) =>
                  setComposeData({ ...composeData, content: e.target.value })
                }
                className="mt-1 min-h-[150px]"
              />
            </div>
            
            {/* Attachment */}
            <div>
              <Label>Attachment (Max 2MB)</Label>
              <div className="mt-1">
                {composeData.attachment ? (
                  <div className="flex items-center justify-between p-3 bg-gray-100 rounded">
                    <div className="flex items-center space-x-2 text-sm">
                      <Paperclip className="w-4 h-4" />
                      <span>{composeData.attachment.name}</span>
                    </div>
                    <Button
                      onClick={() =>
                        setComposeData({ ...composeData, attachment: null })
                      }
                      variant="ghost"
                      size="sm"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Input
                      type="file"
                      id="compose-attachment"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 2 * 1024 * 1024) {
                            toast.error('File size must be less than 2MB');
                            return;
                          }
                          setComposeData({ ...composeData, attachment: file });
                        }
                      }}
                    />
                    <Button
                      onClick={() =>
                        document.getElementById('compose-attachment').click()
                      }
                      variant="outline"
                      className="w-full"
                    >
                      <Paperclip className="w-4 h-4 mr-2" />
                      Attach File
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowCompose(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendMessage}
              disabled={loading || !composeData.content.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagingSystem;
